import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { cn, mapEdgesToNodes } from "../../lib/helpers";
import { CardContext } from "../card/context";
import { Card } from "../card/card";
import { typesMap } from "./resources-list";
import { CarouselCardSwipe } from "./carousel-cardswipe";
import { Carousel } from "./snowflake/carousel";
import { SLIDE_SPEED } from "./tabbed";

const quoteCarouselQuery = graphql`
  query QuoteCarouselQuery {
    docs: allSanityStory(
      sort: { fields: [orderRank], order: ASC }
      filter: {
        status: { eq: "published" }
        slug: { current: { ne: null } }
        statistics: { elemMatch: { number: { ne: null } } }
      }
    ) {
      edges {
        node {
          ...cardNodeStory
          _rawBody(resolveReferences: { maxDepth: 2 })
          company {
            mainImageMonoSolid {
              ...SanityImageSimple
            }
          }
        }
      }
    }
  }
`;

const hasBodyVideo = (story) =>
  story._rawBody?.slice(0, 5).some((block) => block._type === "externalVideo");

const hasVideo = (story) => !!story.mainVideo?.url || hasBodyVideo(story);

const CARD_CONTEXT_OVERRIDES = {
  hideImage: true,
  hideType: true,
  hideDate: true,
  showByline: false,
  isTall: true,
  title: false,
  hideLabels: true,
};

export const QuoteCarousel = ({ section }) => {
  const { docs } = useStaticQuery(quoteCarouselQuery) || {};
  const allStories = section.category
    ? mapEdgesToNodes(docs).filter((d) =>
      d.categories?.find((c) => c.slug.current === section.category)
    )
    : mapEdgesToNodes(docs).sort((a, b) => !!b.statistics?.[0]?.number - !!a.statistics?.[0]?.number);

  const seenCompanies = new Set();
  const stories = allStories.filter((s) => {
    const id = s.company?._id;
    if (!id) return true; // no company — allow through, just won't appear in logo nav
    if (seenCompanies.has(id)) return false;
    seenCompanies.add(id);
    return true;
  });

  const sortedStories = [...stories].sort((a, b) => hasVideo(b) - hasVideo(a));
  const cappedStories = sortedStories.slice(0, section.cards || 12);

  if (!cappedStories.length) return null;

  const cta = typesMap.get("story").cta;

  if (section.layout === "carousel") {
    const slides = cappedStories.map((s, i) => (
      <CardContext.Provider
        key={i}
        value={{ ...s, ...CARD_CONTEXT_OVERRIDES, cta }}
      >
        <div className="relative max-w-screen-md mx-auto group" style={{ minHeight: 330 }}>
          <Card className="text-xl leading-relaxed" />
          {hasVideo(s) && (
            <div className="hidden md:block absolute top-6 right-6 pointer-events-none z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                fill="currentColor"
                className="w-8 h-8 opacity-20 text-black group-hover:text-link group-hover:opacity-80 transition-all"
              >
                <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm115.7 272l-176 101c-15.8 8.8-35.7-2.5-35.7-21V152c0-18.4 19.8-29.8 35.7-21l176 107c15.6 8.4 15.6 30.5 0 39z" />
              </svg>
            </div>
          )}
        </div>
      </CardContext.Provider>
    ));
    const companies = cappedStories.map((s) => s.company).filter(Boolean);
    return <Carousel slides={slides} companies={companies} skipButtons={true} />;
  }

  return (
    <CarouselCardSwipe
      header={section.header}
      docs={cappedStories}
      speed={SLIDE_SPEED * 2}
      renderItem={({ doc, i }) => (
        <CardContext.Provider
          key={i}
          value={{ ...doc, ...CARD_CONTEXT_OVERRIDES, cta }}
        >
          <div
            className={cn(
              "border border-dark-10 rounded-xl pb-8 px-5 mr-4 md:mr-0",
              section.isDarkBg ? "bg-light-10" : "bg-white",
            )}
          >
            <Card />
          </div>
        </CardContext.Provider>
      )}
    />
  );
};
